import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { fetchLevels, createLevel, updateLevel, deleteLevel } from '../services/api';

const LevelList = () => {
  const navigate = useNavigate();
  const [levels, setLevels] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [formData, setFormData] = useState({
    level_name: '',
    level: '',
    type: '',
    designation: '',
    offshore_per_hr: '',
    onsite_per_hr: '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    loadLevels();
  }, []);

  const loadLevels = async () => {
    try {
      const data = await fetchLevels();
      setLevels(data);
    } catch (err) {
      setError('Failed to load levels');
    }
  };

  const handleOpenDialog = (level = null) => {
    if (level) {
      setEditingLevel(level);
      setFormData({
        level_name: level.level_name,
        level: level.level,
        type: level.type,
        designation: level.designation,
        offshore_per_hr: level.offshore_per_hr,
        onsite_per_hr: level.onsite_per_hr,
      });
    } else {
      setEditingLevel(null);
      setFormData({
        level_name: '',
        level: '',
        type: '',
        designation: '',
        offshore_per_hr: '',
        onsite_per_hr: '',
      });
    }
    setError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingLevel(null);
    setFormData({
      level_name: '',
      level: '',
      type: '',
      designation: '',
      offshore_per_hr: '',
      onsite_per_hr: '',
    });
    setError('');
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!formData.level_name) {
      setError('Level Name is required');
      return;
    }
    try {
      if (editingLevel) {
        await updateLevel(editingLevel.id, formData);
      } else {
        await createLevel(formData);
      }
      handleCloseDialog();
      loadLevels();
    } catch (err) {
      setError(err.message || 'Failed to save level');
    }
  };

  const handleDelete = async (level) => {
    if (window.confirm('Are you sure you want to delete this level?')) {
      try {
        await deleteLevel(level.id);
        loadLevels();
      } catch (err) {
        alert('Failed to delete level');
      }
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Level List</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/levels/add')}>
          Add Level
        </Button>
      </Box>
      <Paper sx={{ p: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Level Name</TableCell>
                <TableCell>Level</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Designation</TableCell>
                <TableCell>Offshore Per Hr</TableCell>
                <TableCell>Onsite Per Hr</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {levels.map((level) => (
                <TableRow key={level.id}>
                  <TableCell>{level.level_name}</TableCell>
                  <TableCell>{level.level}</TableCell>
                  <TableCell>{level.type}</TableCell>
                  <TableCell>{level.designation}</TableCell>
                  <TableCell>{level.offshore_per_hr}</TableCell>
                  <TableCell>{level.onsite_per_hr}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenDialog(level)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(level)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingLevel ? 'Edit Level' : 'Add New Level'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Level Name"
              name="level_name"
              value={formData.level_name}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="Level"
              name="level"
              value={formData.level}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Type"
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Designation"
              name="designation"
              value={formData.designation}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Offshore Per Hr"
              name="offshore_per_hr"
              value={formData.offshore_per_hr}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
              type="number"
              inputProps={{ min: 0 }}
            />
            <TextField
              fullWidth
              label="Onsite Per Hr"
              name="onsite_per_hr"
              value={formData.onsite_per_hr}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
              type="number"
              inputProps={{ min: 0 }}
            />
            {error && <Typography color="error">{error}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingLevel ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LevelList; 